// src/pages/container_rental/components/IssueRoundDialog.tsx

import { useEffect } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import dayjs from "dayjs";
import {
  issueRoundSchema,
  IssueRoundValues,
} from "../../../contracts/container_rental/schemas.ts";

interface IssueRoundDialogProps {
  open: boolean;
  round: number;
  onClose: () => void;
  onSubmit: (values: IssueRoundValues) => Promise<void> | void;
  loading?: boolean;
}

export default function IssueRoundDialog({
  open,
  round,
  onClose,
  onSubmit,
  loading = false,
}: IssueRoundDialogProps) {
  const today = dayjs().format("YYYY-MM-DD");

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<IssueRoundValues>({
    resolver: zodResolver(issueRoundSchema),
    defaultValues: {
      issuedAt: today,
      invoiceNumber: "",
      issuedMemo: "",
    },
  });

  // Reset whenever the dialog re-opens.
  useEffect(() => {
    if (open) {
      reset({ issuedAt: today, invoiceNumber: "", issuedMemo: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = handleSubmit(async (values) => {
    await onSubmit(values);
  });

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="issue-round-title"
    >
      <DialogTitle id="issue-round-title">{round}회차 발행 완료 처리</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Controller
            name="invoiceNumber"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="세금계산서 번호"
                required
                fullWidth
                error={Boolean(errors.invoiceNumber)}
                helperText={errors.invoiceNumber?.message}
              />
            )}
          />
          <Controller
            name="issuedAt"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                type="date"
                label="실제 발행일"
                required
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={Boolean(errors.issuedAt)}
                helperText={errors.issuedAt?.message}
              />
            )}
          />
          <Controller
            name="issuedMemo"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="발행 메모 (선택)"
                multiline
                minRows={2}
                fullWidth
                error={Boolean(errors.issuedMemo)}
                helperText={errors.issuedMemo?.message}
              />
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          취소
        </Button>
        <Button onClick={submit} variant="contained" disabled={loading}>
          {loading ? "처리 중..." : "발행 완료"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
